import { pathFor, type RouteId } from "../../../app/router/routes";
import { useLang } from "../../../shared/i18n";
import { Reveal } from "../Reveal";
import { SECTION_IDS, type MarketingCopy } from "../copy";
import { MARKETING_GAMES, type MarketingGame } from "../games";
import { ArrowRightIcon, LockIcon } from "../icons";
import { CtaLink } from "./Cta";
import { Section, SectionHeading } from "./primitives";

export interface GamesShowcaseProps {
  t: MarketingCopy;
}

/**
 * Where a card goes: its own page if it has one, Connect if it does not.
 *
 * ⚠️ THE CAST IS SAFE BECAUSE THE ROUTE TABLE USES THE GAME'S ID AS THE PAGE'S
 * ROUTE ID — `routes.test.ts` round-trips every one — and `hasPage` is only true
 * for the four in `GAME_PAGE_IDS`, each of which has a route.
 */
export function gameHref(game: MarketingGame): string {
  return game.hasPage ? pathFor(game.id as RouteId) : pathFor("contact-sales");
}

/**
 * The games, as a bento: the one free game large, the rest around it.
 *
 * 🔴 SKY QUEST IS LARGE BECAUSE IT IS THE ONLY ONE A STRANGER CAN PLAY, not
 * because it is the best. A reader who wants proof gets it in one tap.
 *
 * ⚠️ A CARD IS A "STRETCHED LINK", NOT AN `<a>` AROUND EVERYTHING. The featured
 * card carries its own "Play free now" button, and a link inside a link is
 * invalid HTML that browsers repair unpredictably. So the card's link is an
 * empty `<a>` stretched over the card (`absolute inset-0`), and the button sits
 * above it as a sibling.
 */
export function GamesShowcase({ t }: GamesShowcaseProps) {
  const [featured, ...rest] = MARKETING_GAMES;

  return (
    <div className="mt-24 rounded-t-[2.5rem] bg-canvas pt-20 pb-28 lg:mt-32 lg:rounded-t-[3.5rem] lg:pt-28 lg:pb-36">
      <Section id={SECTION_IDS.games}>
        <SectionHeading
          label={t.games.label}
          heading={t.games.heading}
          accentClass="bg-leaf"
          labelClass="text-success"
        />

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-[1.35fr_1fr_1fr] lg:grid-rows-[repeat(2,minmax(18rem,auto))] lg:gap-6">
          {featured ? <FeaturedCard t={t} game={featured} /> : null}
          {rest.map((game, i) => (
            <Reveal key={game.id} delay={(i + 1) * 70} className="h-full">
              <GameCard t={t} game={game} />
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
}

function FeaturedCard({ t, game }: { t: MarketingCopy; game: MarketingGame }) {
  const { lang } = useLang();
  return (
    <Reveal className="h-full sm:col-span-2 lg:col-span-1 lg:row-span-2">
      <article className="group relative isolate flex h-full min-h-[32rem] overflow-hidden rounded-2xl sm:min-h-[26rem] shadow-[0_30px_60px_-28px_color-mix(in_srgb,var(--color-sky)_70%,var(--color-ink))]">
        <img
          src={game.image}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover transition-transform duration-500 ease-dash group-hover:scale-[1.03]"
        />
        <div aria-hidden="true" className="kq-card-scrim absolute inset-0 -z-10" />
        <a
          href={gameHref(game)}
          aria-label={t.games.openPage(game.title)}
          className="absolute inset-0 z-0 rounded-2xl outline-none focus-visible:ring-4 focus-visible:ring-primary focus-visible:ring-inset"
        />
        <div className="kq-on-photo pointer-events-none relative z-10 mt-auto w-full p-6 text-surface sm:p-8">
          <SkillChip t={t} game={game} />
          <h3 className="mt-3 font-kid text-kid-lg leading-none font-extrabold sm:text-kid-xl">{game.title}</h3>
          <p className="mt-2 font-dash text-dash-sm font-bold text-surface/90">
            {game.content.levels.map((l) => l.name[lang]).join(" · ")}
          </p>
          <div className="pointer-events-auto mt-5 flex flex-wrap items-center gap-3">
            <CtaLink href={pathFor("sky-quest-play")} variant="sun" size="md" icon="play">
              {t.cta.playFreeNow}
            </CtaLink>
            <span className="rounded-pill border border-surface/50 bg-surface/15 px-3.5 py-1.5 font-dash text-dash-xs font-extrabold">
              {t.games.freeBadge} · {t.games.ages}
            </span>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

/** A small game card — the home page's grid and a game page's "more worlds" row. */
export function GameCard({ t, game }: { t: MarketingCopy; game: MarketingGame }) {
  const { lang } = useLang();
  return (
    <a
      href={gameHref(game)}
      aria-label={game.free || game.hasPage ? t.games.openPage(game.title) : t.games.lockedLabel(game.title)}
      className={`group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card outline-none transition duration-200 ease-dash hover:-translate-y-1 hover:shadow-raised focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${game.ringClass}`}
    >
      <div className={`relative aspect-[16/10] overflow-hidden ${game.tintClass}`}>
        <img
          src={game.image}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-300 ease-dash group-hover:scale-[1.05]"
        />
        {game.free ? (
          <span
            aria-hidden="true"
            className="absolute bottom-3 left-3 inline-flex items-center rounded-pill bg-success px-3 py-1 font-dash text-dash-xs font-extrabold text-surface shadow-card"
          >
            {t.games.freeBadge}
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-pill bg-ink/75 px-3 py-1 font-dash text-dash-xs font-bold text-surface backdrop-blur-sm"
          >
            <LockIcon className="h-3.5 w-3.5" />
            {t.games.lockedBadge}
          </span>
        )}
      </div>
      <div className="flex flex-1 items-end justify-between gap-3 p-4 sm:p-5">
        <div>
          <SkillChip t={t} game={game} light />
          <h3 className="mt-2 font-kid text-kid-sm leading-tight font-extrabold text-ink">{game.title}</h3>
          <p className="mt-0.5 font-dash text-dash-xs font-semibold text-muted">
            {game.content.levels
              .slice(0, 2)
              .map((l) => l.name[lang])
              .join(" · ")}
          </p>
        </div>
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill border border-line bg-canvas text-primary transition-colors duration-150 ease-dash group-hover:border-primary group-hover:bg-primary group-hover:text-primary-ink"
        >
          <ArrowRightIcon className="h-4 w-4" />
        </span>
      </div>
    </a>
  );
}

/** The skill a game teaches, in that skill's colour. */
export function SkillChip({ t, game, light = false }: { t: MarketingCopy; game: MarketingGame; light?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1 font-dash text-dash-xs font-extrabold ${
        light ? `${game.tintClass} text-ink` : "bg-surface/95 text-ink"
      }`}
    >
      <span aria-hidden="true" className={`h-2 w-2 rounded-pill ${game.dotClass}`} />
      {t.skills[game.skill]}
    </span>
  );
}
